import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { coalescedInvalidation } from '@d-orca/shared/client/hooks';
import { fetchCommittedActivities, type ICommittedActivity } from '@apis/activities';
import { ActivitiesStream } from '@apis/activitiesStream';
import { committedActivitiesKey } from '@constants/queryKeys';
import { retryUnlessProviderError } from '@utils/queryRetry';

// ---------------------------------------------------------------------------
// The Activities & Issues page's feed: the site's COMMITTED activities
// (2026-09-17 task-commit design). A row exists because somebody promised the
// work — parent rows, milestones and uncommitted leaves are simply absent, so
// the page is as long as what the site committed to, not as long as the
// imported programme.
//
// LIVE: one SSE stream carries both Trusstor's activity frames and other
// managers' overlay edits, and every frame is an invalidation — the page
// refetches its single joined read rather than merging frames into rows.
//
// NO CACHE ON PURPOSE: when Trusstor is unreachable the read 502s and the page
// says so (invariant 7). `staleTime: 0` keeps a refocus honest for the same
// reason — a status nobody can vouch for is worse than an error.
// ---------------------------------------------------------------------------

export interface ICommittedActivitiesState {
  activities: ICommittedActivity[];
  isLoading: boolean;
  isError: boolean;
  /** True once a feed has loaded at least once — lets the page keep showing
   *  rows through a failed background refetch instead of blanking. */
  hasData: boolean;
}

// One empty feed for every render before the first answer — a fresh `[]` each
// time would bust every memo keyed on `activities` downstream.
const NO_ACTIVITIES: ICommittedActivity[] = [];

export function useCommittedActivities(siteId: string | undefined): ICommittedActivitiesState {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: committedActivitiesKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchCommittedActivities(siteId!, signal),
    enabled: !!siteId,
    staleTime: 0,
    retry: retryUnlessProviderError,
  });

  useEffect(() => {
    if (!siteId) return;
    return joinLiveFeed(qc, siteId);
  }, [siteId, qc]);

  return {
    activities: query.data ?? NO_ACTIVITIES,
    isLoading: query.isLoading,
    isError: query.isError,
    hasData: query.data != null,
  };
}

// ---------------------------------------------------------------------------
// ONE stream per site, however many surfaces read the feed. The Activities page
// alone mounts this hook three times (list, drawer bootstrap, progress
// tracker); a stream each would open three EventSources and turn every frame
// into three refetches racing to cancel one another. The first reader opens the
// stream and the last one to leave closes it.
// ---------------------------------------------------------------------------

interface ILiveFeed {
  readers: number;
  close: () => void;
}

/** Per QueryClient, then per site — a frame's refetch has to land in the cache
 *  its readers read from. */
const liveFeeds = new WeakMap<QueryClient, Map<string, ILiveFeed>>();

function joinLiveFeed(qc: QueryClient, siteId: string): () => void {
  const feeds = liveFeeds.get(qc) ?? new Map<string, ILiveFeed>();
  liveFeeds.set(qc, feeds);
  const feed = feeds.get(siteId) ?? { readers: 0, close: openLiveFeed(qc, siteId) };
  feeds.set(siteId, feed);
  feed.readers += 1;
  return () => {
    feed.readers -= 1;
    if (feed.readers > 0) return;
    feeds.delete(siteId);
    feed.close();
  };
}

/** Open the site's stream and turn its frames into refetches of the feed —
 *  coalesced, so a burst costs two reads (`coalescedInvalidation`). */
function openLiveFeed(qc: QueryClient, siteId: string): () => void {
  const refetch = coalescedInvalidation(qc, committedActivitiesKey(siteId));

  const stream = new ActivitiesStream(siteId, {
    // Any frame — Trusstor's or another manager's edit — means "refetch".
    onEvent: refetch,
    // The stream reconnects itself with a refreshed session; what it may have
    // missed while disconnected is covered by the refetch on reconnect.
    onOpen: refetch,
    onTokenExpiring: () => {},
    onClose: () => {},
    onError: () => {},
  });
  stream.open();
  return () => stream.close();
}
